/**
 * session.ts — orchestrates a live remote session for the viewer (SPEC §4).
 *
 * 1. POST /sessions { device_id, mode } → signal_url + signal_token + turn.
 * 2. Open the signaling WS; the agent is already connected and is the OFFERER.
 * 3. Build the WebRTCViewer (answerer); surface the H.264 track + data channels.
 * 4. Expose channels (control/input/clipboard/shell/files/sysinfo) to the UI.
 *
 * This is framework-agnostic so it can be unit-tested without React.
 */

import type {
  ChannelLabel,
  ConnStats,
  ControlMessage,
  SessionMode,
  TurnCredentials,
} from '@/types';
import type { ApiClient } from '@/api/client';
import { SignalingClient } from '@/lib/signaling';
import { WebRTCViewer } from '@/lib/webrtc';
import type { SessionKeys } from '@/lib/e2ee';
import { pinnedCARoot, requirePinEnforced, verifyNoiseAttestation } from '@/lib/e2ee';

/**
 * Thrown internally to abort startSession when C1 attestation policy fails with a
 * CA root pinned (fail closed). cb.onError has already fired before this throws;
 * callers' try/catch surfaces the message and the session never starts.
 */
class SessionAbort extends Error {
  constructor() {
    super('session aborted: agent encryption-key attestation failed (fail closed)');
    this.name = 'SessionAbort';
  }
}

export interface SessionCallbacks {
  onTrack: (stream: MediaStream) => void;
  onChannel: (label: ChannelLabel, ch: RTCDataChannel) => void;
  onControl?: (msg: ControlMessage) => void;
  onStats?: (stats: ConnStats) => void;
  onSecure?: () => void;
  /** High-level connection lifecycle (connecting / connected / reconnecting / failed). */
  onConnState?: (state: 'connecting' | 'connected' | 'reconnecting' | 'failed') => void;
  onError?: (message: string) => void;
  onEnded?: () => void;
}

export interface ActiveSession {
  sessionId: string;
  mode: SessionMode;
  viewer: WebRTCViewer;
  signaling: SignalingClient;
  turn: TurnCredentials;
  sessionKeys: () => SessionKeys | null;
  sendControl: (msg: ControlMessage) => void;
  channel: (label: ChannelLabel) => RTCDataChannel | undefined;
  end: () => void;
}

export async function startSession(
  api: ApiClient,
  deviceId: string,
  mode: SessionMode,
  cb: SessionCallbacks,
): Promise<ActiveSession> {
  // STRICT mode (opt-in, VITE_HELM_REQUIRE_PIN): refuse to start an UNPINNED session
  // before any server side effect — the strong E2EE-vs-server posture for shops that
  // have set up out-of-band pinning. This is NOT the default: a fresh self-hosted
  // deployment that hasn't pinned a CA root must still be usable, so by default an
  // unpinned session proceeds after verifying the agent key against the SERVED root
  // (relay defense) with a loud warning. Set VITE_HELM_CA_ROOT to pin out-of-band,
  // and VITE_HELM_REQUIRE_PIN=true to enforce it.
  const pin = pinnedCARoot();
  if (requirePinEnforced() && !pin) {
    cb.onError?.(
      'E2EE refused: VITE_HELM_REQUIRE_PIN is set but no CA root is pinned ' +
        '(set VITE_HELM_CA_ROOT). Refusing to start (fail closed).',
    );
    throw new SessionAbort();
  }

  const start = await api.startSession(deviceId, mode);

  // C1 (SPEC §10.1) — cert-bind the agent's Noise static key before trusting it.
  //
  // The agent's Noise X25519 static key (dev.noise_pubkey) is broker-relayed and
  // therefore attacker-substitutable: a malicious/compromised broker could swap
  // it and MITM the "encrypted" session. The CA root signs (device_id,
  // noise_pubkey); we verify that attestation (dev.noise_attestation) against the
  // CA root before pinning the key as the Noise_XK responder static.
  //
  //   - CA root PINNED (VITE_HELM_CA_ROOT set): the SERVED root MUST equal the
  //     pin AND the attestation MUST verify. ANY failure → do not start the
  //     session (cb.onError + throw). FAIL CLOSED. This defends against BOTH a
  //     compromised relay AND a compromised server.
  //   - STRICT + UNPINNED (VITE_HELM_REQUIRE_PIN set, no pin): already failed closed
  //     at the top of this function, before any server call.
  //   - UNPINNED (default): verify the attestation against the SERVED CA root and
  //     PROCEED. This defends against a compromised RELAY (it can't forge a
  //     CA-signed binding) but NOT a compromised server. Proceeding keeps an
  //     un-pinned deployment usable; warn loudly when even the served root can't
  //     verify the key. Pin out-of-band (VITE_HELM_CA_ROOT) for server defense.
  //
  // A missing/empty noise_pubkey leaves agentNoisePubKeyHex undefined → the
  // viewer's beginE2EE fails closed (no cleartext channels), as before.
  let agentNoisePubKeyHex: string | undefined;

  try {
    const dev = await api.getDevice(deviceId);
    const noisePubKeyHex = dev.noise_pubkey;
    const attestationHex = dev.noise_attestation;

    if (typeof noisePubKeyHex !== 'string' || noisePubKeyHex.length === 0) {
      // No agent Noise key surfaced. Leave unpinned → E2EE handshake will fail
      // closed in the viewer (channels stay unusable). Do not open cleartext.
      if (pin) {
        cb.onError?.(
          'Cannot verify agent encryption key: device has no Noise public key. Refusing to start (fail closed).',
        );
        throw new SessionAbort();
      }
      console.warn(
        '[e2ee] device has no Noise public key; E2EE cannot start (fail closed).',
      );
    } else {
      // Fetch the CA root (served by the control plane). Required when pinned.
      let servedRoot: string | null = null;
      try {
        servedRoot = await api.getCARootPubKey();
      } catch {
        servedRoot = null;
      }

      if (pin) {
        // --- PINNED: strict, fail closed on any mismatch/failure. ---
        if (!servedRoot) {
          cb.onError?.(
            'Cannot verify agent encryption key: CA root unavailable. Refusing to start (fail closed).',
          );
          throw new SessionAbort();
        }
        if (servedRoot.trim().toLowerCase() !== pin.toLowerCase()) {
          cb.onError?.(
            'Agent encryption-key attestation rejected: served CA root does not match the pinned root. Refusing to start (fail closed).',
          );
          throw new SessionAbort();
        }
        const ok =
          typeof attestationHex === 'string' &&
          (await verifyNoiseAttestation(deviceId, noisePubKeyHex, attestationHex, pin));
        if (!ok) {
          cb.onError?.(
            'Agent encryption-key attestation failed to verify against the pinned CA root. Refusing to start (fail closed).',
          );
          throw new SessionAbort();
        }
        agentNoisePubKeyHex = noisePubKeyHex;
      } else {
        // --- UNPINNED (default, no out-of-band pin): verify against the SERVED CA
        // root best-effort, then PROCEED. Verifying the attestation against the root
        // helm-server serves over TLS already defeats a compromised RELAY (coturn) —
        // the relay can't forge a CA-signed (device_id, noise_pubkey) binding. It
        // does NOT defend against a compromised SERVER (which could serve its own
        // root + a matching forged attestation); set VITE_HELM_CA_ROOT to pin
        // out-of-band for that, or VITE_HELM_REQUIRE_PIN to refuse unpinned sessions.
        // We proceed either way so an un-pinned deployment is usable, warning loudly
        // when the served root can't even verify the attestation.
        const ok =
          servedRoot != null &&
          typeof attestationHex === 'string' &&
          (await verifyNoiseAttestation(deviceId, noisePubKeyHex, attestationHex, servedRoot));
        if (!ok) {
          console.warn(
            '[e2ee] agent Noise key is UNVERIFIED against the served CA root. ' +
              'Pin a root with VITE_HELM_CA_ROOT. Proceeding with an unverified key.',
          );
        } else {
          console.warn(
            '[e2ee] agent Noise key verified against the SERVED CA root (relay defense only). ' +
              'Set VITE_HELM_CA_ROOT to also defend against a compromised server.',
          );
        }
        agentNoisePubKeyHex = noisePubKeyHex;
      }
    }
  } catch (err) {
    if (err instanceof SessionAbort) {
      // Fail closed: surface already emitted via cb.onError. Do not start.
      throw err;
    }
    // Device detail / CA fetch failed for non-policy reasons. When pinned we must
    // NOT fall through to an unverified key; fail closed. When unpinned, leave
    // agentNoisePubKeyHex undefined so the viewer's handshake fails closed.
    if (pin) {
      cb.onError?.(
        'Cannot verify agent encryption key (device lookup failed). Refusing to start (fail closed).',
      );
      throw new SessionAbort();
    }
    /* unpinned: handshake will fail closed in the viewer if no key */
  }

  // Holder lets the signaling onReconnect reach the viewer, which is constructed
  // after the SignalingClient (the viewer needs the signaling instance).
  const ref: { viewer: WebRTCViewer | null } = { viewer: null };

  const signaling = new SignalingClient({
    url: start.signal_url,
    token: start.signal_token,
    sessionId: start.session_id,
    onError: (p) => cb.onError?.(`${p.code}: ${p.message}`),
    onClose: (ev) => {
      // A clean (1000) close after session_end ends the session; an unexpected
      // close is handled by the signaling client's own reconnect (no onEnded).
      if (ev.code === 1000) cb.onEnded?.();
    },
    onReconnect: () => {
      cb.onConnState?.('reconnecting');
      ref.viewer?.onSignalingReconnect();
    },
  });

  const viewer = new WebRTCViewer({
    signaling,
    turn: start.turn,
    ...(agentNoisePubKeyHex ? { agentNoisePubKeyHex } : {}),
    onTrack: cb.onTrack,
    onChannel: cb.onChannel,
    ...(cb.onControl ? { onControl: cb.onControl } : {}),
    ...(cb.onStats ? { onStats: cb.onStats } : {}),
    ...(cb.onSecure ? { onSecure: cb.onSecure } : {}),
    ...(cb.onConnState ? { onConnState: cb.onConnState } : {}),
  });
  ref.viewer = viewer;

  signaling.connect();
  viewer.start();

  const end = () => {
    viewer.close();
    signaling.close();
    cb.onEnded?.();
  };

  return {
    sessionId: start.session_id,
    mode: start.mode,
    viewer,
    signaling,
    turn: start.turn,
    sessionKeys: () => viewer.sessionKeys(),
    sendControl: (m) => viewer.sendControl(m),
    channel: (l) => viewer.channel(l),
    end,
  };
}
